import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Wallet, Target, Megaphone, SlidersHorizontal, ShieldCheck, MapPin } from 'lucide-react';
import { StepAudience } from './AudienceStep';
import Button from '../ui/Button';
import { StepBudget, StepGoal, StepCampaigns, StepWork, StepGuardrails, CURRENCIES } from './SetupSteps';
import type { Guardrails, EligibleCampaign, TrafficManager, TrafficManagerInput } from '../../services/trafficManagerService';

export { CURRENCIES };

export interface FormState {
  name: string;
  currency: string;
  monthly_budget: string;
  goal: string;
  target_cpa: string;
  target_roas: string;
  mode: 'autopilot' | 'approval';
  instructions: string;
  times_of_day: string[];
  campaign_ids: string[];
  guardrails: Guardrails;
}

/** Moeda padrão: a mais comum entre as campanhas publicadas. */
export const defaultCurrency = (eligible: EligibleCampaign[]): string => {
  const conta: Record<string, number> = {};
  for (const c of eligible) if (c.currency) conta[c.currency] = (conta[c.currency] || 0) + 1;
  const top = Object.entries(conta).sort((a, b) => b[1] - a[1])[0];
  return top && CURRENCIES.includes(top[0]) ? top[0] : 'BRL';
};

export const formFrom = (m: TrafficManager | null, defaults: Guardrails, defaultName: string, fallbackCurrency = 'BRL'): FormState => ({
  name: m?.name || defaultName,
  currency: m?.currency || fallbackCurrency,
  monthly_budget: m ? String(parseFloat(String(m.monthly_budget)) || '') : '',
  goal: m?.goal || 'leads',
  target_cpa: m?.target_cpa ? String(m.target_cpa) : '',
  target_roas: m?.target_roas ? String(m.target_roas) : '',
  mode: m?.mode || 'approval',
  instructions: m?.instructions || '',
  times_of_day: m?.times_of_day?.length ? m.times_of_day : ['09:00'],
  campaign_ids: m?.campaign_ids || [],
  guardrails: { ...defaults, ...(m?.guardrails_effective || {}) }
});

export const toApi = (f: FormState, language: string): TrafficManagerInput => ({
  name: f.name,
  currency: f.currency,
  monthly_budget: parseFloat(f.monthly_budget),
  goal: f.goal as TrafficManagerInput['goal'],
  target_cpa: f.target_cpa ? parseFloat(f.target_cpa) : null,
  target_roas: f.goal === 'sales' && f.target_roas ? parseFloat(f.target_roas) : null,
  mode: f.mode,
  instructions: f.instructions,
  times_of_day: f.times_of_day,
  campaign_ids: f.campaign_ids,
  guardrails: f.guardrails,
  language
});

export const validate = (f: FormState, t: (k: string, d: string) => string): string | null => {
  if (!(parseFloat(f.monthly_budget) > 0)) return t('trafficManager.form.budgetRequired', 'Informe quanto quer gastar por mês');
  if (!f.times_of_day.length) return t('trafficManager.form.timeRequired', 'Escolha ao menos um horário');
  return null;
};

type Tab = 'budget' | 'goal' | 'campaigns' | 'audience' | 'work' | 'guardrails';

/** Configuração do gestor já ativo: as mesmas peças do assistente, em abas. */
const ManagerForm: React.FC<{
  initial: FormState;
  eligible: EligibleCampaign[];
  saving: boolean;
  onSave: (f: FormState) => void;
  onCancel?: () => void;
  submitLabel: string;
}> = ({ initial, eligible, saving, onSave, onCancel, submitLabel }) => {
  const { t } = useTranslation();
  const [f, setF] = useState<FormState>(initial);
  const [tab, setTab] = useState<Tab>('budget');
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const setG = <K extends keyof Guardrails>(k: K, v: Guardrails[K]) => setF((s) => ({ ...s, guardrails: { ...s.guardrails, [k]: v } }));
  const tabs: [Tab, string, React.ElementType][] = [
    ['budget', t('trafficManager.setup.stepBudget', 'Verba'), Wallet],
    ['goal', t('trafficManager.setup.stepGoal', 'Objetivo'), Target],
    ['campaigns', t('trafficManager.setup.stepCampaigns', 'Campanhas'), Megaphone],
    ['audience', t('trafficManager.setup.stepAudience', 'Público'), MapPin],
    ['work', t('trafficManager.setup.stepWork', 'Como trabalhar'), SlidersHorizontal],
    ['guardrails', t('trafficManager.setup.guardrailsTitle', 'Travas de segurança'), ShieldCheck]
  ];
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate(f, t);
    if (err) { toast.error(err); return; }
    onSave(f);
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <nav className="tm-tabs flex gap-1 overflow-x-auto overflow-y-hidden -mx-1 px-1 shadow-[inset_0_-1px_0_#e5e7eb] dark:shadow-[inset_0_-1px_0_#374151]" role="tablist">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 shrink-0 ${tab === k ? 'border-primary-600 text-primary-700 dark:text-primary-300 font-medium' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </nav>
      <div className="min-h-[360px]">
        {tab === 'budget' && <StepBudget f={f} set={set} eligible={eligible} />}
        {tab === 'goal' && <StepGoal f={f} set={set} />}
        {tab === 'campaigns' && <StepCampaigns f={f} set={set} eligible={eligible} />}
        {tab === 'audience' && <StepAudience f={f} setG={setG} />}
        {tab === 'work' && <StepWork f={f} set={set} />}
        {tab === 'guardrails' && <StepGuardrails f={f} setG={setG} />}
      </div>
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
        {onCancel && <Button type="button" variant="secondary" onClick={onCancel}>{t('common.cancel', 'Cancelar')}</Button>}
        <Button type="submit" isLoading={saving}>{submitLabel}</Button>
      </div>
    </form>
  );
};

export default ManagerForm;
